import React, { useState, useCallback } from 'react';
import { View, Text, StyleSheet, FlatList, TouchableOpacity, RefreshControl, Image, ScrollView, Linking, Platform } from 'react-native';
import { useLocalSearchParams, useRouter, useFocusEffect, Stack } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { api } from '../../../src/api/client';
import { useToast } from '../../../src/components/Toast';
import Card from '../../../src/components/Card';
import Button from '../../../src/components/Button';
import InlineConfirm from '../../../src/components/InlineConfirm';
import ContentContainer from '../../../src/components/ContentContainer';
import { useTheme } from '../../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';
import type { ColorTheme } from '../../../src/utils/theme';
import { SvgXml } from 'react-native-svg';
import * as Clipboard from 'expo-clipboard';
import { useResponsive } from '../../../src/utils/responsive';

interface Bay {
  id: string;
  name: string;
  type: 'self-serve' | 'automatic' | 'vacuum';
  pricePerMinute: number | null;
  minimumCharge: number | null;
  maximumCharge: number | null;
  tiers: { name: string; price: number; enabled?: boolean; durationMs?: number }[] | null;
  isActive: boolean;
  qrCodeUrl: string;
  deepLink: string;
  live?: { inUse: boolean; outOfService: boolean; testing: boolean };
  equipment?: { controllers: number; online: number; updateRequired: boolean; configured?: boolean };
}

interface VendingMachine {
  id: string;
  name: string;
  equipment?: { controllers: number; online: number; updateRequired: boolean; configured?: boolean };
  relayIndex: number;
  creditStepCents: number;
  presets: number[] | null;
  maxCreditCents: number;
  controllerId: string | null;
  isActive: boolean;
  qrCodeUrl: string | null;
  deepLink: string;
}

interface Location {
  id: string;
  name: string;
  companyName: string | null;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string | null;
  description: string | null;
  hours: any;
  isActive: boolean;
}

/** LocationDetailScreen - location info, bay list with QR codes. */
export default function LocationDetailScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const { width: screenWidth } = useResponsive();
  const { id } = useLocalSearchParams<{ id: string }>();
  const [location, setLocation] = useState<Location | null>(null);
  const [bays, setBays] = useState<Bay[]>([]);
  const [machines, setMachines] = useState<VendingMachine[]>([]);
  // Round 2 (S06): manual Advanced actions shown as buttons under their equipment.
  const [equipButtons, setEquipButtons] = useState<{ equipmentKind: 'bay' | 'vending'; equipmentId: string; actionId: string; name: string }[]>([]);
  const [runningButton, setRunningButton] = useState<string | null>(null);
  const runEquipButton = async (b: { equipmentKind: 'bay' | 'vending'; equipmentId: string; actionId: string; name: string }) => {
    setRunningButton(b.actionId);
    try { await api(`/api/${b.equipmentKind === 'bay' ? 'bays' : 'vending-machines'}/${b.equipmentId}/actions/${b.actionId}/run`, { method: 'POST', body: {} }); toast.show({ type: 'success', title: `${b.name} started` }); }
    catch (err: any) { toast.show({ type: 'error', title: 'Could not run', message: err.message }); }
    finally { setRunningButton(null); }
  };
  const ButtonsFor = ({ kind, id: eqId }: { kind: 'bay' | 'vending'; id: string }) => {
    const rows = equipButtons.filter(b => b.equipmentKind === kind && b.equipmentId === eqId);
    if (!rows.length) return null;
    return (
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginTop: spacing.sm }}>
        {rows.map(b => <Button key={b.actionId} title={runningButton === b.actionId ? 'Running…' : b.name} size="sm" variant="outline" disabled={!!runningButton} onPress={() => runEquipButton(b)} icon={<Ionicons name="flash-outline" size={14} color={colors.primary} />} />)}
      </View>
    );
  };
  const [loading, setLoading] = useState(true);
  const [showAddBay, setShowAddBay] = useState(false);
  const [deleteBayConfirm, setDeleteBayConfirm] = useState<{ id: string; name: string } | null>(null);
  const [deleteBayLoading, setDeleteBayLoading] = useState(false);
  const [deleteMachineConfirm, setDeleteMachineConfirm] = useState<{ id: string; name: string } | null>(null);
  const [deleteMachineLoading, setDeleteMachineLoading] = useState(false);
  const router = useRouter();
  const toast = useToast();

  const fetchData = async (silent = false) => {
    try {
      const [loc, bayList] = await Promise.all([
        api<Location>(`/api/locations/${id}`),
        api<Bay[]>(`/api/locations/${id}/bays`),
      ]);
      setLocation(loc);
      setBays(bayList);
      api<{ buttons: typeof equipButtons }>(`/api/locations/${id}/equipment-buttons`).then(r => setEquipButtons(r.buttons ?? [])).catch(() => setEquipButtons([]));
      // Vending machines are additive — a fetch failure must not blank the bays.
      try {
        const machineList = await api<VendingMachine[]>(`/api/locations/${id}/vending-machines`);
        setMachines(machineList);
      } catch { /* silent: section simply stays empty */ }
    } catch (err: any) {
      if (!silent) toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setLoading(false);
    }
  };

  useFocusEffect(useCallback(() => { fetchData(); const t = setInterval(() => fetchData(true), 15000); return () => clearInterval(t); }, [id])); // 1.4 P19: live chips refresh while focused (silent: no toast storm when offline, 10-16 review M15)

  const confirmDeleteBay = async (bayId: string) => {
    setDeleteBayLoading(true);
    try {
      await api(`/api/bays/${bayId}`, { method: 'DELETE' });
      setDeleteBayConfirm(null);
      fetchData();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setDeleteBayLoading(false);
    }
  };

  const confirmDeleteMachine = async (machineId: string) => {
    setDeleteMachineLoading(true);
    try {
      await api(`/api/vending-machines/${machineId}`, { method: 'DELETE' });
      setDeleteMachineConfirm(null);
      fetchData();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setDeleteMachineLoading(false);
    }
  };

  return (
    <>
      <Stack.Screen options={{
        title: location?.name || 'Location',
        headerRight: () => (
          <TouchableOpacity onPress={() => router.push(`/owner/location/edit?locationId=${id}` as never)} hitSlop={12} style={{ padding: 12 }}>
            <Ionicons name="create-outline" size={28} color={colors.primary} />
          </TouchableOpacity>
        ),
      }} />
      <ScrollView style={styles.container} refreshControl={<RefreshControl refreshing={loading} onRefresh={fetchData} />}>
        <ContentContainer>
        {location && (
          <Card style={styles.infoCard}>
            {location.companyName && (
              <View style={styles.infoRow}>
                <Ionicons name="business" size={18} color={colors.textSecondary} />
                <Text style={styles.infoText}>{location.companyName}</Text>
              </View>
            )}
            <View style={styles.infoRow}>
              <Ionicons name="location" size={18} color={colors.textSecondary} />
              <Text style={styles.infoText}>{location.address}, {location.city}, {location.state} {location.zip}</Text>
            </View>
            {location.phone && (
              <View style={styles.infoRow}>
                <Ionicons name="call" size={18} color={colors.textSecondary} />
                <Text style={styles.infoText}>{location.phone}</Text>
              </View>
            )}
            {location.description && (
              <View style={styles.infoRow}>
                <Ionicons name="document-text" size={18} color={colors.textSecondary} />
                <Text style={styles.infoText}>{location.description}</Text>
              </View>
            )}
            {/* Google review prompt status at a glance; the link itself is set on Edit. */}
            <TouchableOpacity
              style={styles.infoRow}
              onPress={() => router.push(`/owner/location/edit?locationId=${id}` as never)}
            >
              <Ionicons name="logo-google" size={18} color={(location as any).googleReviewUrl ? colors.success : colors.textLight} />
              <Text style={[styles.infoText, !(location as any).googleReviewUrl && { color: colors.textLight, fontStyle: 'italic' }]}>
                {(location as any).googleReviewUrl
                  ? 'Google review prompt on — 5-star raters get a gentle ask'
                  : 'Google review link not set — tap to add it in Edit'}
              </Text>
              <Ionicons name="chevron-forward" size={16} color={colors.textLight} />
            </TouchableOpacity>
          </Card>
        )}

        {/* ── Equipment — bays (automatic / self-serve / vacuum) + vending
            machines merged into one section with a single add flow. ── */}
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Equipment ({bays.length + machines.length})</Text>
          <TouchableOpacity onPress={() => router.push(`/owner/location/add-equipment?locationId=${id}` as never)}>
            <View style={styles.addBtn}>
              <Ionicons name="add" size={28} color={colors.primary} />
              <Text style={styles.addText}>Add Equipment</Text>
            </View>
          </TouchableOpacity>
        </View>

        {bays.map(bay => (
          <Card key={bay.id} style={styles.bayCard}>
            <View style={styles.bayHeader}>
              <View style={styles.bayIcon}>
                <Ionicons
                  name={bay.type === 'automatic' ? 'car' : bay.type === 'vacuum' ? 'sparkles' : 'timer'}
                  size={20}
                  color={colors.primary}
                />
              </View>
              <View style={styles.bayInfo}>
                <Text style={styles.bayName}>{bay.name}</Text>
                <Text style={styles.bayType}>
                  {bay.type === 'automatic' ? 'Automatic' : bay.type === 'vacuum' ? 'Vacuum' : 'Self-Serve'}
                </Text>
                {/* 1.4 P19: live equipment chips */}
                <View style={styles.chipRow}>
                  {bay.live?.outOfService && <View style={[styles.chip, styles.chipDanger]}><Text style={styles.chipText}>Out of service</Text></View>}
                  {!bay.live?.outOfService && bay.live?.inUse && <View style={[styles.chip, styles.chipInfo]}><Text style={styles.chipText}>{bay.live?.testing ? 'Testing' : 'In use'}</Text></View>}
                  {!!bay.equipment?.controllers && bay.equipment.online === 0 && <View style={[styles.chip, styles.chipDanger]}><Text style={styles.chipText}>Offline</Text></View>}
                  {!!bay.equipment?.controllers && bay.equipment.online > 0 && bay.equipment.online < bay.equipment.controllers && <View style={[styles.chip, styles.chipWarn]}><Text style={styles.chipText}>Partly offline</Text></View>}
                  {bay.equipment?.updateRequired && <View style={[styles.chip, styles.chipWarn]}><Text style={styles.chipText}>Update required</Text></View>}
                  {bay.equipment && bay.equipment.controllers === 0 && <View style={[styles.chip, styles.chipWarn]}><Text style={styles.chipText}>Edit to assign controller</Text></View>}
                  {bay.equipment && bay.equipment.controllers > 0 && bay.equipment.configured === false && <View style={[styles.chip, styles.chipWarn]}><Text style={styles.chipText}>No relay assigned</Text></View>}
                </View>
              </View>
              <View style={styles.bayActions}>
                <TouchableOpacity onPress={() => router.push(`/owner/location/edit-bay?bayId=${bay.id}` as never)} style={{ marginRight: spacing.md, padding: spacing.xs }}>
                  <Ionicons name="create-outline" size={26} color={colors.primary} />
                </TouchableOpacity>
                <TouchableOpacity onPress={() => setDeleteBayConfirm({ id: bay.id, name: bay.name })} style={{ padding: spacing.xs }}>
                  <Ionicons name="trash-outline" size={26} color={colors.danger} />
                </TouchableOpacity>
              </View>
            </View>
            <ButtonsFor kind="bay" id={bay.id} />

            {bay.type === 'self-serve' && bay.pricePerMinute && (
              <View style={styles.pricing}>
                <View style={styles.priceItem}>
                  <Text style={styles.priceLabel}>Rate:</Text>
                  <Text style={styles.priceValue}>${bay.pricePerMinute.toFixed(2)}/min</Text>
                </View>
                {bay.minimumCharge != null && (
                  <View style={styles.priceItem}>
                    <Text style={styles.priceLabel}>Min:</Text>
                    <Text style={styles.priceValue}>${(bay.minimumCharge / 100).toFixed(2)}</Text>
                  </View>
                )}
                {bay.maximumCharge != null && (
                  <View style={styles.priceItem}>
                    <Text style={styles.priceLabel}>Max:</Text>
                    <Text style={styles.priceValue}>${(bay.maximumCharge / 100).toFixed(2)}</Text>
                  </View>
                )}
              </View>
            )}

            {(bay.type === 'automatic' || bay.type === 'vacuum') && bay.tiers && bay.tiers.some(t => t.enabled !== false) && (
              <View style={styles.tiers}>
                {bay.tiers.filter(t => t.enabled !== false).map((tier, i) => (
                  <View key={i} style={styles.tierRow}>
                    <Text style={styles.tierName}>
                      {tier.name}
                      {bay.type === 'vacuum' && tier.durationMs ? ` · ${Math.round(tier.durationMs / 60000)} min` : ''}
                    </Text>
                    <Text style={styles.tierPrice}>${(tier.price / 100).toFixed(2)}</Text>
                  </View>
                ))}
              </View>
            )}

            <InlineConfirm
              message={`Are you sure you want to delete "${bay.name}"? This action is permanent and cannot be undone. The QR code and wash link for this bay will stop working. If recreated, the bay will have a new QR code.`}
              visible={deleteBayConfirm?.id === bay.id}
              onConfirm={() => confirmDeleteBay(bay.id)}
              onCancel={() => setDeleteBayConfirm(null)}
              confirmLabel="Delete Bay"
              loading={deleteBayLoading}
              variant="danger"
            />

            {bay.qrCodeUrl && (() => {
              const qrMaxWidth = Math.min(screenWidth - spacing.md * 4, 400);
              let svgXml = '';
              let qrHeight = Math.round(qrMaxWidth * (700 / 480)); // fallback
              try {
                const b64 = bay.qrCodeUrl.replace(/^data:image\/svg\+xml;base64,/, '');
                svgXml = atob(b64);
                const vbMatch = svgXml.match(/viewBox="0\s+0\s+([\d.]+)\s+([\d.]+)"/);
                if (vbMatch) {
                  const vbW = parseFloat(vbMatch[1]);
                  const vbH = parseFloat(vbMatch[2]);
                  if (vbW > 0) qrHeight = Math.round(qrMaxWidth * (vbH / vbW));
                }
              } catch { /* non-critical: SVG viewBox parse for QR sizing */ }
              return (
              <View style={styles.qrContainer}>
                {Platform.OS === 'web' ? (
                  <Image source={{ uri: bay.qrCodeUrl }} style={[styles.qrImage, { width: qrMaxWidth, height: qrHeight }]} resizeMode="contain" />
                ) : (
                  <SvgXml
                    xml={svgXml}
                    width={qrMaxWidth}
                    height={qrHeight}
                    style={styles.qrImage}
                  />
                )}
                <Text style={styles.qrUrl} selectable>{bay.deepLink}</Text>
                <View style={styles.qrActions}>
                  <TouchableOpacity
                    style={styles.downloadBtn}
                    onPress={async () => {
                      try {
                        if (Platform.OS === 'web') {
                          await navigator.clipboard.writeText(bay.deepLink);
                        } else {
                          await Clipboard.setStringAsync(bay.deepLink);
                        }
                        toast.show({ type: 'success', title: 'Copied', message: 'Wash URL copied to clipboard' });
                      } catch { /* clipboard API unavailable or denied */
                        toast.show({ type: 'error', title: 'Copy failed' });
                      }
                    }}
                  >
                    <Ionicons name="copy-outline" size={18} color={colors.primary} />
                    <Text style={styles.downloadText}>Copy URL</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.downloadBtn}
                    onPress={async () => {
                      try {
                        const { downloadFile } = await import('../../../src/utils/fileExport');
                        await downloadFile(bay.qrCodeUrl, `rinsepay-qr-${bay.name.replace(/\s+/g, '-')}.svg`);
                      } catch (err) {
                        console.error('QR download failed:', err);
                      }
                    }}
                  >
                    <Ionicons name="download-outline" size={18} color={colors.primary} />
                    <Text style={styles.downloadText}>Download QR</Text>
                  </TouchableOpacity>
                </View>
              </View>
              );
            })()}
          </Card>
        ))}

        {machines.map(m => (
          <Card key={m.id} style={styles.bayCard}>
            <View style={styles.bayHeader}>
              <View style={styles.bayIcon}>
                <Ionicons name="fast-food" size={20} color={colors.primary} />
              </View>
              <View style={styles.bayInfo}>
                <Text style={styles.bayName}>{m.name}</Text>
                <Text style={styles.bayType}>
                  Vending{m.isActive ? '' : ' · Inactive'}
                </Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: 2 }}>
                  {!!m.equipment?.controllers && m.equipment.online === 0 && <View style={[styles.chip, styles.chipDanger]}><Text style={styles.chipText}>Offline</Text></View>}
                  {m.equipment?.updateRequired && <View style={[styles.chip, styles.chipWarn]}><Text style={styles.chipText}>Update required</Text></View>}
                  {m.equipment && m.equipment.controllers === 0 && <View style={[styles.chip, styles.chipWarn]}><Text style={styles.chipText}>Edit to assign controller</Text></View>}
                  {m.equipment && m.equipment.controllers > 0 && m.equipment.configured === false && <View style={[styles.chip, styles.chipWarn]}><Text style={styles.chipText}>No relay assigned</Text></View>}
                </View>
              </View>
              <View style={styles.bayActions}>
                <TouchableOpacity onPress={() => router.push(`/owner/location/edit-vending?machineId=${m.id}` as never)} style={{ marginRight: spacing.md, padding: spacing.xs }}>
                  <Ionicons name="create-outline" size={26} color={colors.primary} />
                </TouchableOpacity>
                <TouchableOpacity onPress={() => setDeleteMachineConfirm({ id: m.id, name: m.name })} style={{ padding: spacing.xs }}>
                  <Ionicons name="trash-outline" size={26} color={colors.danger} />
                </TouchableOpacity>
              </View>
            </View>
            <ButtonsFor kind="vending" id={m.id} />

            <View style={styles.pricing}>
              <View style={styles.priceItem}>
                <Text style={styles.priceLabel}>Relay:</Text>
                <Text style={styles.priceValue}>{m.relayIndex}</Text>
              </View>
              <View style={styles.priceItem}>
                <Text style={styles.priceLabel}>Step:</Text>
                <Text style={styles.priceValue}>{m.creditStepCents}¢</Text>
              </View>
              <View style={styles.priceItem}>
                <Text style={styles.priceLabel}>Max:</Text>
                <Text style={styles.priceValue}>${(m.maxCreditCents / 100).toFixed(2)}</Text>
              </View>
              {m.presets && m.presets.length > 0 && (
                <View style={styles.priceItem}>
                  <Text style={styles.priceLabel}>Presets:</Text>
                  <Text style={styles.priceValue}>{m.presets.map(p => `$${(p / 100).toFixed(2)}`).join(', ')}</Text>
                </View>
              )}
            </View>

            <InlineConfirm
              message={`Delete "${m.name}"? This is permanent — its QR code and vending link will stop working.`}
              visible={deleteMachineConfirm?.id === m.id}
              onConfirm={() => confirmDeleteMachine(m.id)}
              onCancel={() => setDeleteMachineConfirm(null)}
              confirmLabel="Delete Machine"
              loading={deleteMachineLoading}
              variant="danger"
            />

            {m.qrCodeUrl && (
              <QrBlock
                qrCodeUrl={m.qrCodeUrl}
                deepLink={m.deepLink}
                name={m.name}
                screenWidth={screenWidth}
                styles={styles}
                colors={colors}
                toast={toast}
              />
            )}
          </Card>
        ))}

        {bays.length === 0 && machines.length === 0 && !loading && (
          <View style={styles.empty}>
            <Ionicons name="construct-outline" size={48} color={colors.textLight} />
            <Text style={styles.emptyText}>No equipment configured yet</Text>
          </View>
        )}

        <View style={{ height: spacing.xxl }} />
        </ContentContainer>
      </ScrollView>
    </>
  );
}

/** Shared QR display block (SVG on native, <Image> on web) with copy/download
 *  actions. Module-level so its identity is stable across renders. */
function QrBlock({ qrCodeUrl, deepLink, name, screenWidth, styles, colors, toast }: {
  qrCodeUrl: string;
  deepLink: string;
  name: string;
  screenWidth: number;
  styles: ReturnType<typeof createStyles>;
  colors: ColorTheme;
  toast: ReturnType<typeof useToast>;
}) {
  const qrMaxWidth = Math.min(screenWidth - spacing.md * 4, 400);
  let svgXml = '';
  let qrHeight = Math.round(qrMaxWidth * (700 / 480)); // fallback
  try {
    const b64 = qrCodeUrl.replace(/^data:image\/svg\+xml;base64,/, '');
    svgXml = atob(b64);
    const vbMatch = svgXml.match(/viewBox="0\s+0\s+([\d.]+)\s+([\d.]+)"/);
    if (vbMatch) {
      const vbW = parseFloat(vbMatch[1]);
      const vbH = parseFloat(vbMatch[2]);
      if (vbW > 0) qrHeight = Math.round(qrMaxWidth * (vbH / vbW));
    }
  } catch { /* non-critical: SVG viewBox parse for QR sizing */ }
  return (
    <View style={styles.qrContainer}>
      {Platform.OS === 'web' ? (
        <Image source={{ uri: qrCodeUrl }} style={[styles.qrImage, { width: qrMaxWidth, height: qrHeight }]} resizeMode="contain" />
      ) : (
        <SvgXml xml={svgXml} width={qrMaxWidth} height={qrHeight} style={styles.qrImage} />
      )}
      <Text style={styles.qrUrl} selectable>{deepLink}</Text>
      <View style={styles.qrActions}>
        <TouchableOpacity
          style={styles.downloadBtn}
          onPress={async () => {
            try {
              if (Platform.OS === 'web') {
                await navigator.clipboard.writeText(deepLink);
              } else {
                await Clipboard.setStringAsync(deepLink);
              }
              toast.show({ type: 'success', title: 'Copied', message: 'URL copied to clipboard' });
            } catch { /* clipboard API unavailable or denied */
              toast.show({ type: 'error', title: 'Copy failed' });
            }
          }}
        >
          <Ionicons name="copy-outline" size={18} color={colors.primary} />
          <Text style={styles.downloadText}>Copy URL</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.downloadBtn}
          onPress={async () => {
            try {
              const { downloadFile } = await import('../../../src/utils/fileExport');
              await downloadFile(qrCodeUrl, `rinsepay-qr-${name.replace(/\s+/g, '-')}.svg`);
            } catch (err) {
              console.error('QR download failed:', err);
            }
          }}
        >
          <Ionicons name="download-outline" size={18} color={colors.primary} />
          <Text style={styles.downloadText}>Download QR</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: 4 },
  chip: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 10, borderWidth: 1 },
  chipDanger: { borderColor: c.danger, backgroundColor: c.danger + '15' },
  chipWarn: { borderColor: c.warning, backgroundColor: c.warning + '15' },
  chipInfo: { borderColor: c.primary, backgroundColor: c.primary + '15' },
  chipMuted: { borderColor: c.border, backgroundColor: c.surfaceSecondary },
  chipText: { fontSize: 10, fontWeight: '600', color: c.text },
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  infoCard: { marginBottom: spacing.md },
  infoRow: { flexDirection: 'row', alignItems: 'center', marginBottom: spacing.sm, gap: spacing.sm },
  infoText: { fontSize: fontSize.sm, color: c.textSecondary, flex: 1 },
  sectionHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md, marginTop: spacing.sm },
  sectionTitle: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text },
  addBtn: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  addText: { fontSize: fontSize.sm, color: c.primaryDark, fontWeight: fontWeight.semibold },
  bayCard: { marginBottom: spacing.md },
  bayHeader: { flexDirection: 'row', alignItems: 'center' },
  bayIcon: {
    width: 40, height: 40, borderRadius: 20,
    backgroundColor: c.primaryBg,
    justifyContent: 'center', alignItems: 'center',
    marginRight: spacing.md,
  },
  bayActions: { flexDirection: 'row', alignItems: 'center' },
  bayInfo: { flex: 1 },
  bayName: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text },
  bayType: { fontSize: fontSize.sm, color: c.textSecondary },
  pricing: { flexDirection: 'row', flexWrap: 'wrap', rowGap: spacing.xs, columnGap: spacing.md, marginTop: spacing.md, paddingTop: spacing.md, borderTopWidth: 1, borderTopColor: c.borderLight },
  priceItem: { flexDirection: 'row', alignItems: 'baseline', gap: spacing.xs },
  priceLabel: { fontSize: fontSize.sm, color: c.textSecondary },
  priceValue: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  tiers: { marginTop: spacing.md, paddingTop: spacing.md, borderTopWidth: 1, borderTopColor: c.borderLight },
  tierRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: spacing.xs },
  tierName: { fontSize: fontSize.sm, color: c.text },
  tierPrice: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.primaryDark },
  qrContainer: { alignItems: 'center', marginTop: spacing.md, paddingTop: spacing.md, borderTopWidth: 1, borderTopColor: c.borderLight },
  qrActions: { flexDirection: 'row', gap: spacing.sm, marginTop: spacing.md },
  qrImage: { alignSelf: 'center' as const },
  qrUrl: { fontSize: fontSize.xs, color: c.primaryDark, marginTop: spacing.sm, textAlign: 'center' as const },
  downloadBtn: { flexDirection: 'row' as const, alignItems: 'center' as const, gap: spacing.xs, paddingVertical: spacing.sm, paddingHorizontal: spacing.md, borderRadius: borderRadius.md, borderWidth: 1.5, borderColor: c.primary },
  downloadText: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.primaryDark },
  empty: { alignItems: 'center', paddingTop: spacing.xxl },
  emptyText: { fontSize: fontSize.md, color: c.textLight, marginTop: spacing.md },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
